<script setup lang="ts">
import { NavigationPaginatorProps } from "./Paginator.props"
import { sm as isMobile } from "~/utils/breakpoints"
import { removePageFromUrl, filterTrackingParameters } from "~/utils/url"

const props = defineProps<NavigationPaginatorProps>()
const emit = defineEmits<{ (e: "update:currentPage", page: number): void }>()

const currentPage = ref(props.currentPage ?? 1)
const route = useRoute()

const relPrev = computed(() => (currentPage.value !== 1 ? "prev" : undefined))
const relNext = computed(() =>
  currentPage.value !== props.totalPages ? "next" : undefined
)

const goToPage = (page: number) => {
  if (page >= 1 && page <= props.totalPages) {
    currentPage.value = page
    emit("update:currentPage", page)
  }
}

// Capture phase: scroll before the route guards show the listing skeleton
const scrollToTopOnPageClick = (event: MouseEvent) => {
  const link = (event.target as HTMLElement).closest("a:not(.selected)")
  if (!(link instanceof HTMLElement)) return
  link.blur()
  window.scrollTo({ top: 0, behavior: "auto" })
}

const isSecondPageVisible = computed(() => !(currentPage.value > 3))

const isSecondToLastVisible = computed(
  () => !(currentPage.value < props.totalPages - 2)
)

const arePagesLessEqualThanSix = computed(() => props.totalPages <= 5)

const range = computed<Array<number>>(() => {
  if (arePagesLessEqualThanSix.value) {
    return Array.from({ length: props.totalPages }, (_, index) => index + 1)
  }
  const MAX_LENGTH = isMobile.value ? 1 : 3
  if (isSecondPageVisible.value) {
    return Array.from({ length: MAX_LENGTH }, (_, index) => index - 1 + 4)
  }
  if (isSecondToLastVisible.value) {
    return Array.from(
      { length: MAX_LENGTH },
      (_, index) => props.totalPages - (isMobile.value ? 2 : 4) + index
    )
  }
  return Array.from(
    { length: MAX_LENGTH },
    (_, index) => currentPage.value + (index - (isMobile.value ? 0 : 1))
  )
})

const formatUrl = (page: number) => {
  const pagePath = removeParamsAndAnchors(removePageFromUrl(route.fullPath))
  const query = filterTrackingParameters(route?.fullPath.split("?")[1] ?? null)

  return page === 0
    ? `${pagePath}${query ? `?${query}` : ""}`
    : page === 1
    ? `${pagePath}${query ? `?${query}` : ""}`
    : `${pagePath}/page/${page}${query ? `?${query}` : ""}`
}

const links = [
  ...(currentPage.value !== props.totalPages
    ? [
        {
          rel: "next",
          href: formatUrl(currentPage.value + 1)
        }
      ]
    : []),
  ...(currentPage.value !== 1
    ? [
        {
          rel: "prev",
          href: formatUrl(currentPage.value - 1)
        }
      ]
    : [])
]

useHead({
  link: links
})
</script>

<template>
  <div
    class="paginator border-slate-20 flex h-[56px] rounded-[4px] border py-2"
    @click.capture="scrollToTopOnPageClick"
  >
    <NuxtLink
      aria-label="Navigate previous"
      class="
        paginator__previous-button
        flex
        items-center
        justify-center
        gap-1
        px-4
      "
      :class="[{ 'pointer-events-none cursor-not-allowed': currentPage === 1 }]"
      :to="currentPage === 1 ? formatUrl(0) : formatUrl(currentPage - 1)"
      :disabled="currentPage === 1"
      :rel="relPrev"
    >
      <IconsArrowPaginatorLeft
        class="h-6 w-6"
        :class="[currentPage === 1 ? 'text-slate-40' : 'text-green-main']"
      />
      <p
        class="pig hidden font-medium md:flex"
        :class="[currentPage === 1 ? 'text-slate-40' : 'text-green-main']"
      >
        {{ $t("navigation.paginator.previous") }}
      </p>
    </NuxtLink>
    <div
      class="
        paginator__numeric-pages
        border-slate-20
        flex
        border-l border-r
        px-2
      "
      v-if="arePagesLessEqualThanSix"
    >
      <NuxtLink
        class="numeric-page-button"
        :class="{ selected: currentPage === item }"
        v-for="item in range"
        :to="formatUrl(item)"
      >
        {{ item }}
      </NuxtLink>
    </div>
    <div
      class="
        paginator__numeric-pages
        border-slate-20
        flex
        border-l border-r
        px-2
      "
      v-else
    >
      <NuxtLink
        class="numeric-page-button"
        :class="{ selected: currentPage === 1 }"
        @click="goToPage(1)"
        :to="formatUrl(1)"
      >
        1
      </NuxtLink>
      <NuxtLink
        class="numeric-page-button"
        :class="{
          selected: currentPage === 2,
          'disabled text-slate-40': !isSecondPageVisible
        }"
        :to="formatUrl(2)"
      >
        {{ isSecondPageVisible ? 2 : "..." }}
      </NuxtLink>
      <NuxtLink
        class="numeric-page-button"
        :class="{ selected: currentPage === item }"
        v-for="item in range"
        @click="goToPage(item)"
        :to="formatUrl(item)"
      >
        {{ item }}
      </NuxtLink>
      <NuxtLink
        class="numeric-page-button"
        :class="{
          selected: currentPage === props.totalPages - 1,
          'disabled text-slate-40': !isSecondToLastVisible
        }"
        :to="formatUrl(props.totalPages - 1)"
      >
        {{ isSecondToLastVisible ? props.totalPages - 1 : "..." }}
      </NuxtLink>
      <NuxtLink
        class="numeric-page-button"
        :class="{ selected: currentPage === props.totalPages }"
        :to="formatUrl(props.totalPages)"
      >
        {{ props.totalPages }}
      </NuxtLink>
    </div>
    <NuxtLink
      aria-label="Navigate next"
      class="paginator__next-button flex items-center justify-center gap-1 px-4"
      :to="
        currentPage === props.totalPages
          ? formatUrl(currentPage)
          : formatUrl(currentPage + 1)
      "
      :disabled="currentPage === props.totalPages"
      :class="[
        {
          'pointer-events-none cursor-not-allowed':
            currentPage === props.totalPages
        }
      ]"
      :rel="relNext"
    >
      <p
        class="pig hidden font-medium md:flex"
        :class="[
          currentPage === props.totalPages ? 'text-slate-40' : 'text-green-main'
        ]"
      >
        {{ $t("navigation.paginator.next") }}
      </p>
      <IconsArrowPaginatorRight
        class="h-6 w-6"
        :class="[
          currentPage === props.totalPages ? 'text-slate-40' : 'text-green-main'
        ]"
      />
    </NuxtLink>
  </div>
</template>

<style scoped lang="scss">

.numeric-page-button {
  @apply flex h-10 w-10 items-center justify-center rounded-[4px];
  &.selected {
    @apply bg-green-10 text-green-70 font-bold;
  }
}

</style>
